import { useState, useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { seedDatabase } from '@/services/adminApi';
import type { SeedData } from '@/types';

/**
 * Default seed data URL — points to the seed-tier-data.json in the backend-services
 * data directory. In production this would be served from an S3 bucket or API endpoint.
 * For local development, the file can be loaded via file upload instead.
 */
const DEFAULT_SEED_URL = '/seed-tier-data.json';

interface SeedPreview {
  tiers: number;
  accessRules: number;
  upgradeRules: number;
  csiNameMap: number;
  promotions: number;
  total: number;
}

function computePreview(data: SeedData): SeedPreview {
  return {
    tiers: data.tiers.length,
    accessRules: data.accessRules.length,
    upgradeRules: data.upgradeRules.length,
    csiNameMap: data.csiNameMap.length,
    promotions: data.promotions.length,
    total:
      data.tiers.length +
      data.accessRules.length +
      data.upgradeRules.length +
      data.csiNameMap.length +
      data.promotions.length,
  };
}

export default function SeedPage() {
  const [seedData, setSeedData] = useState<SeedData | null>(null);
  const [preview, setPreview] = useState<SeedPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const loadData = useCallback((data: SeedData) => {
    setSeedData(data);
    setPreview(computePreview(data));
    setResult(null);
  }, []);

  const handleFileUpload = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const parsed = JSON.parse(e.target?.result as string) as SeedData;
          if (!parsed.tiers || !parsed.accessRules || !parsed.upgradeRules || !parsed.csiNameMap) {
            setResult({
              type: 'error',
              message: 'Invalid seed data format. Expected keys: tiers, accessRules, upgradeRules, csiNameMap, promotions.',
            });
            return;
          }
          loadData(parsed);
        } catch {
          setResult({ type: 'error', message: 'Failed to parse JSON file.' });
        }
      };
      reader.readAsText(file);
    },
    [loadData],
  );

  const handleLoadDefault = useCallback(async () => {
    setLoading(true);
    setResult(null);
    try {
      const response = await fetch(DEFAULT_SEED_URL);
      if (!response.ok) {
        throw new Error(
          `Failed to fetch default seed data (${response.status}). ` +
          'Make sure seed-tier-data.json is available in the public directory or upload a file manually.',
        );
      }
      const data = (await response.json()) as SeedData;
      loadData(data);
    } catch (err) {
      setResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to load default seed data.',
      });
    } finally {
      setLoading(false);
    }
  }, [loadData]);

  const handleSeed = useCallback(async () => {
    if (!seedData || !preview) return;

    setSeeding(true);
    setProgress(0);
    setResult(null);

    try {
      // Simulate progress — the actual batch write happens inside seedDatabase
      const progressInterval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 5, 90));
      }, 200);

      const { count } = await seedDatabase(seedData);

      clearInterval(progressInterval);
      setProgress(100);
      setResult({
        type: 'success',
        message: `Successfully seeded ${count} records to DynamoDB.`,
      });
    } catch (err) {
      setResult({
        type: 'error',
        message: err instanceof Error ? err.message : 'Failed to seed database.',
      });
    } finally {
      setSeeding(false);
    }
  }, [seedData, preview]);

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Seed Data
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Load tier data from a JSON file into DynamoDB. This will upsert all records
        (existing records with the same keys will be overwritten).
      </Typography>

      {result && (
        <Alert severity={result.type} sx={{ mb: 3 }} onClose={() => setResult(null)}>
          {result.message}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Load Seed Data
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <Button
              variant="outlined"
              component="label"
              startIcon={<UploadFileIcon />}
              disabled={seeding}
            >
              Upload JSON File
              <input
                type="file"
                accept=".json"
                hidden
                onChange={handleFileUpload}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
            <Button
              variant="outlined"
              startIcon={<CloudDownloadIcon />}
              onClick={handleLoadDefault}
              disabled={loading || seeding}
            >
              {loading ? 'Loading...' : 'Load Default Seed File'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      {preview && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Preview
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText primary="Tiers" secondary={`${preview.tiers} records`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Access Rules" secondary={`${preview.accessRules} records`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Upgrade Rules" secondary={`${preview.upgradeRules} records`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="CSI Name Mappings" secondary={`${preview.csiNameMap} records`} />
              </ListItem>
              <ListItem>
                <ListItemText primary="Promotions" secondary={`${preview.promotions} records`} />
              </ListItem>
              <ListItem>
                <ListItemText
                  primary={<strong>Total</strong>}
                  secondary={`${preview.total} records`}
                />
              </ListItem>
            </List>

            {seeding && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Writing records to DynamoDB... {Math.round(progress)}%
                </Typography>
              </Box>
            )}

            <Box sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<PlayArrowIcon />}
                onClick={handleSeed}
                disabled={seeding || !seedData}
                size="large"
              >
                {seeding ? 'Seeding...' : 'Seed Database'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
